import { useEffect, useMemo, useState } from "react";
import { kaylasApi } from "./kaylasApi";
import { KaylasDrawer, PhotoField, FormSection } from "./KaylasDrawer";
import { Plus, Search, Edit3, Trash2, Loader2, Sprout, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const SEASONS = ["Spring", "Summer", "Autumn", "Winter", "Year-round"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const empty = {
  seed_name: "", variety: "", quantity: 0, source: "",
  sowing_season: "Spring", sowing_month: "", date_sown: "",
  germination_days: "", days_to_maturity: "",
  garden_bed: "", row: "", container: "",
  planted: false, photo: null, notes: "",
};

export default function KaylasSeeds() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawer, setDrawer] = useState(null);

  const load = async () => { setLoading(true); try { setItems(await kaylasApi.listSeeds()); } finally { setLoading(false); } };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await kaylasApi.listSeeds(); if (!cancelled) setItems(r); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const thisMonth = MONTHS[new Date().getMonth()];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items;
    if (filter === "sow-now") list = list.filter((i) => (i.sowing_month || "").toLowerCase() === thisMonth.toLowerCase() && !i.planted);
    else if (filter === "planted") list = list.filter((i) => i.planted);
    else if (filter === "to-sow") list = list.filter((i) => !i.planted);
    return list.filter((i) => (q ? [i.seed_name, i.variety, i.source].some((s) => (s || "").toLowerCase().includes(q)) : true));
  }, [items, search, filter, thisMonth]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this seed record?")) return;
    await kaylasApi.deleteSeed(id);
    toast.success("Seed removed");
    await load();
  };

  const sowNowCount = items.filter((i) => (i.sowing_month || "").toLowerCase() === thisMonth.toLowerCase() && !i.planted).length;

  return (
    <div className="space-y-6" data-testid="mums-seeds-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><Sprout size={12} /> Tab 3</div>
          <h1 className="font-display text-4xl mt-2">Kayla&apos;s Seeds</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">{items.length} packet{items.length === 1 ? "" : "s"} · {sowNowCount} ready to sow this {thisMonth}</p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer("new")} data-testid="mums-seed-add"><Plus size={15} /> Add Seed</button>
      </header>

      {/* Seed Planner banner */}
      {sowNowCount > 0 && (
        <div className="kaylas-card p-4 flex items-start gap-3" data-testid="seed-planner">
          <CalendarDays size={20} className="text-[var(--mum-cobalt)] mt-0.5" />
          <div>
            <h3 className="font-display text-lg text-[var(--mum-sapphire)]">Seed Planner</h3>
            <p className="text-sm text-[var(--mum-cobalt)]">You have <strong>{sowNowCount}</strong> seed packet{sowNowCount === 1 ? "" : "s"} ready to sow this {thisMonth}.</p>
            <button className="mum-btn-ghost mt-2" onClick={() => setFilter("sow-now")} data-testid="seed-planner-view">View now</button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input className="mum-field pl-9" placeholder="Search seeds…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mums-seed-search" />
        </div>
        <select className="mum-field sm:w-56" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="mums-seed-filter">
          <option value="all">All seeds</option>
          <option value="sow-now">Sow this month</option>
          <option value="to-sow">Not yet planted</option>
          <option value="planted">Already planted</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="kaylas-card-quiet p-10 text-center" data-testid="mums-seed-empty">
          <Sprout size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">No seed packets yet.</p>
          <button className="mum-btn mt-5" onClick={() => setDrawer("new")}><Plus size={15} /> Add first packet</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mums-seed-grid">
          {filtered.map((s) => (
            <article key={s.id} className="kaylas-card overflow-hidden flex flex-col" data-testid={`seed-${s.id}`}>
              <div className="aspect-[4/3] bg-[var(--mum-sky)]/50">
                {s.photo ? (
                  <img src={s.photo} alt={s.seed_name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Sprout size={36} className="text-[var(--mum-cobalt)]/50" /></div>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-xl text-[var(--mum-sapphire)]">{s.seed_name}{s.variety ? ` — ${s.variety}` : ""}</h3>
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full ${s.planted ? "bg-[var(--mum-cobalt)] text-[var(--mum-ivory)]" : "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]"}`}>{s.planted ? "Planted" : "To sow"}</span>
                </div>
                <p className="text-xs text-[var(--mum-cobalt)]/75 mt-1">
                  Qty {s.quantity}{s.sowing_season ? ` · ${s.sowing_season}` : ""}{s.sowing_month ? ` · ${s.sowing_month}` : ""}
                </p>
                {s.source && <p className="text-xs text-[var(--mum-cobalt)]/75 mt-0.5">Source: {s.source}</p>}
                {s.notes && <p className="text-sm mt-2 line-clamp-3">{s.notes}</p>}
                <div className="flex gap-2 mt-auto pt-3">
                  <button className="mum-btn-ghost flex-1" onClick={() => setDrawer({ edit: s })} data-testid={`seed-edit-${s.id}`}><Edit3 size={13} /> Edit</button>
                  <button className="mum-btn-ghost" onClick={() => onDelete(s.id)} data-testid={`seed-delete-${s.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <KaylasDrawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.edit ? "Edit Seed" : "Add Seed"} testid="mums-seed-drawer">
        <SeedForm initial={drawer?.edit || empty} onClose={() => setDrawer(null)} onSaved={async () => { setDrawer(null); await load(); }} />
      </KaylasDrawer>
    </div>
  );
}

function SeedForm({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [form, setForm] = useState({ ...empty, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.seed_name.trim()) { toast.error("Seed name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity) || 0,
        germination_days: form.germination_days === "" ? null : Number(form.germination_days),
        days_to_maturity: form.days_to_maturity === "" ? null : Number(form.days_to_maturity),
      };
      if (editing) await kaylasApi.updateSeed(initial.id, payload);
      else await kaylasApi.createSeed(payload);
      toast.success(editing ? "Seed updated" : "Seed added");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save the seed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="seed-form">
      <FormSection title="The Packet">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Seed name *</span>
          <input className="mum-field mt-1.5" value={form.seed_name} onChange={(e) => set("seed_name", e.target.value)} required data-testid="seed-name" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Variety</span>
            <input className="mum-field mt-1.5" value={form.variety || ""} onChange={(e) => set("variety", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Quantity</span>
            <input type="number" min="0" className="mum-field mt-1.5" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="seed-quantity" />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Source</span>
          <input className="mum-field mt-1.5" placeholder="Where it came from" value={form.source || ""} onChange={(e) => set("source", e.target.value)} />
        </label>
      </FormSection>

      <FormSection title="Sowing">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Season</span>
            <select className="mum-field mt-1.5" value={form.sowing_season || ""} onChange={(e) => set("sowing_season", e.target.value)}>
              <option value="">—</option>
              {SEASONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Month</span>
            <select className="mum-field mt-1.5" value={form.sowing_month || ""} onChange={(e) => set("sowing_month", e.target.value)} data-testid="seed-month">
              <option value="">—</option>
              {MONTHS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Sown</span>
            <input type="date" className="mum-field mt-1.5" value={form.date_sown || ""} onChange={(e) => set("date_sown", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Germ. days</span>
            <input type="number" min="0" className="mum-field mt-1.5" value={form.germination_days || ""} onChange={(e) => set("germination_days", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Maturity (days)</span>
            <input type="number" min="0" className="mum-field mt-1.5" value={form.days_to_maturity || ""} onChange={(e) => set("days_to_maturity", e.target.value)} />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Bed</span>
            <input className="mum-field mt-1.5" value={form.garden_bed || ""} onChange={(e) => set("garden_bed", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Row</span>
            <input className="mum-field mt-1.5" value={form.row || ""} onChange={(e) => set("row", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Container</span>
            <input className="mum-field mt-1.5" placeholder="Tray 2 / Pot 5" value={form.container || ""} onChange={(e) => set("container", e.target.value)} />
          </label>
        </div>
        <label className="inline-flex items-center gap-2 mt-1">
          <input type="checkbox" checked={!!form.planted} onChange={(e) => set("planted", e.target.checked)} data-testid="seed-planted" />
          <span className="text-sm">Already planted out</span>
        </label>
      </FormSection>

      <FormSection title="Photo & Notes">
        <PhotoField label="Photo" value={form.photo} onChange={(v) => set("photo", v)} testid="seed-photo" />
        <textarea className="mum-field" rows={3} placeholder="Notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
      </FormSection>

      <div className="flex gap-2 sticky bottom-0 bg-[var(--mum-paper)] pt-3 -mx-5 px-5 pb-1 border-t border-[var(--mum-sky)]">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy} data-testid="seed-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {editing ? "Save changes" : "Add seed"}
        </button>
      </div>
    </form>
  );
}
